import { useEffect, useMemo } from 'react';

import { ConfigFlags } from 'services/file.service';
import { FileStatus, FileType, Rotate } from 'services/file.service';
import { SelectionMethod } from 'services/story.service';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/store-hooks';

import { handleFileConfig, selectSelectedFile } from 'components/app/_mediator/app-files.slice';
import { isFileStatusCompleted, prettyLabel } from 'components/app/_helpers/_app.helper';
import { EffectsAdjustment, setEffectsAdjustment, fetchClips } from 'components/app/editor/_mediator/editor.slice';
import { useSelectorContext } from 'components/app/selector/_mediator/selector.provider';

import { InputCheck } from 'components/UI/input/elements/input-check';
import { CONFIGS_SLIDERS_FILE } from './_config-panel.params';
import { ConfigSlider } from './elements/config-slider';
import { ConfigRotate } from './elements/config-rotate';
import styles from './_editor-config.module.scss';

const CONFIGS = [
  { key: ConfigFlags.reverse, label: ConfigFlags[ConfigFlags.reverse].toString(), forImage: false },
  { key: ConfigFlags.flip, label: ConfigFlags[ConfigFlags.flip].toString(), forImage: true },
  { key: ConfigFlags.stabilization, label: ConfigFlags[ConfigFlags.stabilization].toString(), forImage: false },
];

export const ConfigPanelFile = () => {
  const selectedFile = useAppSelector((state) => state.projects.selectedFile);
  const selectedStory = useAppSelector((state) => state.projects.selectedStory);
  const fileStatuses = useAppSelector((state) => state.app__files.statuses);
  const effectsAdjustment = useAppSelector((state) => state.editor.effectsAdjustment);
  const configs = useAppSelector((state) => state.app__files.configs);
  const highlightsFetchMode = useAppSelector((state) => state.editor.highlightsFetchMode);
  const storyConfig = useAppSelector((state) => state.editor.storyConfig);
  const selectedFileData = useAppSelector(selectSelectedFile);
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const { setIsHighlightSensitivityActive } = useSelectorContext();

  const isImage = selectedFileData?.fileType === FileType.image;

  const fileData = useMemo(() => {
    if (!selectedFile) return;
    return configs[selectedFile];
  }, [configs, selectedFile]);

  const isProcessing = useMemo(() => {
    if (!selectedFile) return;
    return !isFileStatusCompleted(fileStatuses[selectedFile]);
  }, [fileStatuses, selectedFile]);

  const handleConfigs = (flag: ConfigFlags) => {
    if (!fileData) return;
    const flags = fileData.flags.includes(flag)
      ? fileData.flags.filter((val) => val !== flag)
      : [...fileData.flags, flag];

    dispatch(handleFileConfig({ data: { ...fileData, flags }, backupParams: { title: 'File Config' } }));
  };

  const handleEffects = () => {
    if (!fileData) return;
    const effects = store.getState().editor.effectsAdjustment;
    dispatch(handleFileConfig({ data: { ...fileData, ...effects }, backupParams: { title: 'File Effects' } }));
  };

  const handleRotate = (rotate: Rotate | null) => {
    if (!fileData) return;
    switch (rotate) {
      case Rotate.default:
        rotate = null;
        break;
      case null:
        rotate = Rotate.d90;
        break;
      default:
        rotate = rotate + 1;
        if (rotate > Rotate.d270) rotate = Rotate.default;
        break;
    }
    dispatch(handleFileConfig({ data: { ...fileData, rotate: rotate }, backupParams: { title: 'File Rotate' } }));
  };

  useEffect(() => {
    if (!fileData) return;
    dispatch(
      setEffectsAdjustment({
        brightness: fileData.brightness,
        contrast: fileData.contrast,
        volume: fileData.volume,
        highlightSensitivity: fileData.highlightSensitivity,
      })
    );
  }, [fileData]);

  // Re-fetch highlights with all highlights when sensitivity flag is enabled and we don't have all highlights yet
  useEffect(() => {
    if (!selectedStory || !fileData) return;

    const isSensitivityEnabled = fileData.flags?.includes(ConfigFlags.highlightSensitivity);
    if (!isSensitivityEnabled) return;

    // Check if we already have all highlights loaded for this story
    const hasAllHighlights = highlightsFetchMode.storyId === selectedStory && highlightsFetchMode.mode === 'all';

    // If we don't have all highlights, fetch them once
    if (!hasAllHighlights) {
      dispatch(fetchClips({ storyId: selectedStory, includeAllHighlights: true }));
    }
  }, [fileData?.flags, selectedStory, highlightsFetchMode, dispatch]);

  const filteredConfigs = useMemo(() => {
    let configs = isImage ? CONFIGS_SLIDERS_FILE.filter(({ forImage }) => forImage) : CONFIGS_SLIDERS_FILE;

    if (storyConfig?.selectionMethod === SelectionMethod.manual) {
      configs = configs.filter((config) => config.key !== ConfigFlags.highlightSensitivity); // Filter out highlightSensitivity when selection method is Manual
    }

    return configs;
  }, [isImage, storyConfig?.selectionMethod]);

  if (!fileData || selectedFileData?.status === FileStatus.Error) return;

  return (
    <div className={styles.Config}>
      <h1 className={styles.Config__title}>File Config menu</h1>
      <div>
        {(isImage ? CONFIGS.filter(({ forImage }) => forImage) : CONFIGS).map(({ key, label }) => (
          <InputCheck
            key={key}
            value={fileData.flags.includes(key)}
            setValue={() => handleConfigs(key)}
            title={label}
            loading={isProcessing}
          />
        ))}
        <ConfigRotate rotate={fileData.rotate} onClick={() => handleRotate(fileData.rotate)} />

        <div className={styles.Config__divider} />

        {filteredConfigs.map(({ key, label, params }) => (
          <div key={key}>
            <InputCheck
              value={fileData.flags.includes(key)}
              setValue={() => handleConfigs(key)}
              loading={isProcessing}
              title={prettyLabel(label)}
            />
            {fileData.flags.includes(key) && (
              <ConfigSlider
                params={params}
                value={effectsAdjustment[label as keyof EffectsAdjustment]}
                setValue={(value) => !isProcessing && dispatch(setEffectsAdjustment({ [label]: value }))}
                onSubmit={handleEffects}
                configKey={key}
                onHoverChange={setIsHighlightSensitivityActive}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
